import {
  Box,
  Flex,
  Heading,
  Text,
  Skeleton,
  useToast,
  Link,
  HStack,
  Spacer,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  SimpleGrid,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import {
  useParams,
  useNavigate,
  Link as ReactRouterLink,
} from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';

import { LenderReceiptStatusBadge } from '../Status';
import { z } from 'zod/v4';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { DownloadActionButton } from '~/core/routes/deliverables/[deliverableId]/DownloadActionButton';
import { DeliverableReceiptsInputTable } from '~/core/routes/deliverables/[deliverableId]/final-output-report-versions/[receiptId]/DeliverableReceiptsInputTable';
import { DigitalMonthlyReportContainer } from '~/core/routes/deliverables/[deliverableId]/final-output-report-versions/[receiptId]/digitalMonthlyReport/DigitalMonthlyReportContainer';
import { useCreateDeliverableContainerReceiptActionMutation } from '~/core/routes/deliverables/[deliverableId]/final-output-report-versions/[receiptId]/mutations/CreateDeliverableContainerReceiptAction.gql.generated';
import {
  GetDeliverableContainerReceiptsDocumentGQLType,
  useGetDeliverableContainerReceiptsQuery,
} from '~/core/routes/deliverables/[deliverableId]/queries/GetDeliverableContainerReceipts.gql.generated';
import { SingleEntityDrawer as DeliverableEventLogSingleEntityDrawer } from '~/core/routes/deliverables/EntityEventLogDrawers';
import {
  CommentEditorInput,
  CommentEditorCancelButton,
  CommentEditorCreateButton,
  CommentEditor,
} from '~/packages/comments/CommentEditor';
import { useCreateThreadWithCommentMutation } from '~/packages/comments/mutations/CreateThreadWithComment.gql.generated';
import { useGetThreadsByDomainIdentifierQuery } from '~/packages/comments/queries/GetThreadsByDomainIdentifier.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { useSearchParamsDisclosure } from '~/packages/common-frontend/hooks/useSearchParamDisclosure';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM } from '~/packages/event-logs/constants';

// Types
const RouteParamsStruct = z.object({
  deliverableId: z.string({ message: 'Missing URL parameter' }),
  receiptId: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
type ConfirmationModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (comment: string) => void;
  status: 'approve' | 'remediationRequested';
  borrowerName: string;
};
const ConfirmationModal = ({
  isOpen,
  onCancel,
  onSubmit,
  status,
  borrowerName,
}: ConfirmationModalProps) => {
  const cancelRef = React.useRef(null);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <CommentEditor onCreate={onSubmit}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {commonUtils.toTitleCase(status)}
            </AlertDialogHeader>
            <AlertDialogBody display="flex" flexDirection="column" gap="1rem">
              <Typography variant="base-muted">
                {status === 'approve' &&
                  `${borrowerName} will be notified that this report was approved`}
                {status === 'remediationRequested' &&
                  `A notice will be sent to ${borrowerName} regarding your request for remediation.`}
              </Typography>
              <CommentEditorInput
                isEditable
                placeHolderText={
                  status === 'approve'
                    ? 'Optional comments'
                    : `Specify the actions you want ${borrowerName} to take`
                }
              />
            </AlertDialogBody>
            <AlertDialogFooter display="flex" flexDirection="row" gap="1rem">
              <CommentEditorCancelButton
                size="md"
                isDisabled={false}
                onCancel={onCancel}
              >
                Cancel
              </CommentEditorCancelButton>
              <CommentEditorCreateButton size="md" isDisabled={false}>
                {commonUtils.toTitleCase(status)}
              </CommentEditorCreateButton>
            </AlertDialogFooter>
          </CommentEditor>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

// lender page where you see the details of a receipt & can approve/requestRemediation
const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const navigate = useNavigate();
  const toast = useToast();

  const commentsDrawerDisclosure = useSearchParamsDisclosure({
    key: EVENT_LOG_DRAWER_DISCLOSURE_SEARCH_PARAM,
  });

  const params = useParams();
  const { deliverableId, receiptId } = RouteParamsStruct.parse(params);

  const {
    data: receiptsData,
    error: receiptsError,
    refetch: refetchReceipts,
  } = useGetDeliverableContainerReceiptsQuery({
    fetchPolicy: 'network-only',
    variables: {
      containerId: deliverableId,
    },
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const [
    createDeliverableContainerReceiptActionMutation,
  ] = useCreateDeliverableContainerReceiptActionMutation({
    fetchPolicy: 'network-only',
    refetchQueries: [
      {
        query: GetDeliverableContainerReceiptsDocumentGQLType,
        variables: { containerId: deliverableId },
      },
    ],
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const threadsQuery = useGetThreadsByDomainIdentifierQuery({
    variables: {
      domain: 'deliverables_container',
      domainIdentifier: deliverableId,
    },
  });

  const [createThreadWithComment] = useCreateThreadWithCommentMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });
  const [
    lenderConfirmationModalAction,
    setLenderConfirmationModalAction,
  ] = useState<'approve' | 'remediationRequested' | undefined>();

  const linkColor = useColorModeValue('gray.800', 'gray.200');
  const auroraButtonColor = useColorModeValue(colors.aurora[400], 'white');
  const approveButtonColorScheme = useColorModeValue('aurora', 'gray');

  if (threadsQuery.error) throw threadsQuery.error;
  if (!receiptsData || !threadsQuery.data) {
    return <Skeleton height="600px" />;
  }

  if (receiptsData.deliverableContainerReceipts.length === 0) {
    toast({ title: 'No existing versions.' });
    navigate(`/deliverables`);
  }

  if (receiptsError) {
    toast({ title: `Failed to load page.` });
    navigate('/deliverables');
  }

  const receipts = receiptsData.deliverableContainerReceipts;
  const currentReceipt = receipts.find((r) => r.id === receiptId);

  if (!currentReceipt) {
    toast({ title: 'Could not find version with this id.' });
    navigate(`/deliverables`);
    return <Skeleton height="600px" />;
  }

  return (
    <Box height="100%">
      <Flex justify="space-between" flexDirection="column">
        <Text fontSize="xs" color={linkColor} letterSpacing="wide" pb="16px">
          <Link as={ReactRouterLink} to="/deliverables" fontWeight="semibold">
            Deliverables
          </Link>{' '}
          {`> ${user.organization.name} - ${commonUtils.toTitleCase(
            currentReceipt.containerName,
          )}`}
        </Text>
        <HStack pb="16px" spacing={4}>
          <Heading as="h2" fontSize="3xl">
            {`${commonUtils.toTitleCase(
              currentReceipt.containerName,
            )} for ${commonDate.fullDateStrToShortMonthDayYearString(
              currentReceipt.containerDueDate,
            )}`}
          </Heading>
          {currentReceipt.expired ? (
            <Tooltip
              label="This version is expired as a more recent version exists."
              aria-label="expired-tooltip"
              placement="right"
            >
              <HStack
                alignItems="center"
                px="14px"
                py="10px"
                borderRadius="8px"
                bgColor="gray.100"
                width="fit-content"
              >
                <Icon icon="calendar-circle-exclamation" color="gray.500" />
                <Text
                  color="gray.500"
                  fontSize="sm"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  Expired Version
                </Text>
              </HStack>
            </Tooltip>
          ) : (
            <LenderReceiptStatusBadge
              mostRecentAction={currentReceipt.actionsLog[0].action}
            />
          )}

          <Spacer />
          <DeliverableEventLogSingleEntityDrawer
            entityId={deliverableId}
            receiptId={receiptId}
            disclosure={commentsDrawerDisclosure}
            externalOrgOptions={[
              {
                value: currentReceipt.borrowerId,
                label: currentReceipt.borrowerName,
              },
            ]}
          />
          <DownloadActionButton currentReceipt={currentReceipt} />
        </HStack>

        <SimpleGrid columns={3} spacingX={10} spacingY="4px" mb={8} mt={8}>
          <Text fontSize="xs" color="gray.600">
            Credit Facility
          </Text>
          <Text fontSize="xs" color="gray.600">
            Deliverable Type
          </Text>
          <Text fontSize="xs" color="gray.600">
            Due Date
          </Text>

          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(currentReceipt.creditFacilitySlug)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(currentReceipt.containerType)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonDate.fullDateStrToHumanDateString(
              currentReceipt.containerDueDate,
            )}
          </Text>
        </SimpleGrid>

        {currentReceipt.containerType === 'settlementReport' &&
        currentReceipt.finalArtifactsInstances.length > 0 ? (
          <DigitalMonthlyReportContainer currentReceipt={currentReceipt} />
        ) : (
          <>
            <Heading as="h2" fontSize="24px" mb="24px">
              Inputs
            </Heading>
            <DeliverableReceiptsInputTable currentReceipt={currentReceipt} />
          </>
        )}

        {lenderConfirmationModalAction && (
          <ConfirmationModal
            isOpen={!!lenderConfirmationModalAction}
            status={lenderConfirmationModalAction}
            borrowerName={currentReceipt.borrowerName}
            onCancel={() => {
              setLenderConfirmationModalAction(undefined);
            }}
            onSubmit={async (comment) => {
              let commentId: string | undefined;
              if (comment.length > 0) {
                const {
                  data: newThreadWithCommentData,
                } = await createThreadWithComment({
                  variables: {
                    participatingOrganizationIds: [
                      currentReceipt.borrowerId,
                      user.organization.id,
                    ],
                    comment,
                    domain: 'deliverables_container',
                    domainIdentifier: deliverableId,
                  },
                });
                finleyAssert(
                  newThreadWithCommentData &&
                    newThreadWithCommentData.thread.comments.length > 0,
                  `[Lender] Missing newThreadWithCommentData for mutation [createNewThreadWithCommentForDeliverableContainerMutation] [threadId: ${newThreadWithCommentData?.thread.threadId}]`,
                );
                commentId = newThreadWithCommentData.thread.comments[0].id;

                // do not need to call notifyNewCommentForDeliverableContainerMutation here because, comment is sent as part of "approved/request remediation email"
                threadsQuery.refetch();
              }

              // mutation
              await createDeliverableContainerReceiptActionMutation({
                variables: {
                  userId: user.id,
                  organizationId: user.organization.id,
                  receiptId: currentReceipt.id,
                  commentId,
                  action:
                    lenderConfirmationModalAction === 'approve'
                      ? 'approved'
                      : 'requestRemediation',
                },
              });
              // refetch receipts
              refetchReceipts();

              // close modal
              setLenderConfirmationModalAction(undefined);
            }}
          />
        )}
        {!currentReceipt.expired &&
          currentReceipt.actionsLog[0].action === 'submitted' && (
            <Box mt={8}>
              <HStack spacing="20px">
                <Spacer />
                <Button
                  py={5}
                  px={5}
                  justifyContent="flex-start"
                  variant="outline"
                  size="sm"
                  borderColor={auroraButtonColor}
                  borderWidth="2px"
                  color={auroraButtonColor}
                  _focus={{ boxShadow: 'none' }}
                  onClick={() => {
                    setLenderConfirmationModalAction('remediationRequested');
                  }}
                >
                  Remediation Requested
                </Button>

                <Button
                  py={5}
                  px={6}
                  ml={4}
                  justifyContent="flex-start"
                  variant="solid"
                  _focus={{ boxShadow: 'none' }}
                  size="sm"
                  colorScheme={approveButtonColorScheme}
                  color="white"
                  onClick={() => {
                    setLenderConfirmationModalAction('approve');
                  }}
                >
                  Approve
                </Button>
              </HStack>
            </Box>
          )}
      </Flex>
    </Box>
  );
};

// Exports
export const Route = () => <RouteBase />;
